import { useTranslation } from "react-i18next";
import { Icon, Loader } from "@atoms";
import {
  getConnectsWorkdaysByUserId,
  useAppSelector,
  getUser,
  isLoadingConnectWorkdaysByUserId,
  useAppDispatch,
  actions,
  isErrorConnectWorkdaysByUserId,
} from "@lib/store";
import { useEffect, useMemo } from "react";
import { isOfficeWorkday } from "@lib/store/src/data/timeslots/utils";
import { startOfWeek, endOfWeek, subWeeks } from "date-fns";
import { dateKeyForDateTimeString, dateToLocalDateString } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { WEEKS_BACK } from "./constants";

type DayAggregate = {
  hasOffice: boolean;
};

const weekStartConfig = { weekStartsOn: 1 } as const; // Monday

const now = new Date();

// Compute the window for the last 4 full completed weeks
const currentWeekStart = startOfWeek(now, weekStartConfig);

const windowStartDate = startOfWeek(subWeeks(currentWeekStart, WEEKS_BACK), weekStartConfig);
const windowEndDate = endOfWeek(subWeeks(currentWeekStart, 1), weekStartConfig);

const LAST_WEEKS_DATE_RANGE = {
  startDate: dateToLocalDateString(windowStartDate),
  endDate: dateToLocalDateString(windowEndDate),
} as const;

export const Cards = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const userId = useAppSelector(state => getUser(state).id);

  const isLoading = useAppSelector(state =>
    isLoadingConnectWorkdaysByUserId(state, { userId, dateRange: LAST_WEEKS_DATE_RANGE }),
  );

  const isError = useAppSelector(state =>
    isErrorConnectWorkdaysByUserId(state, { userId, dateRange: LAST_WEEKS_DATE_RANGE }),
  );

  const userWorkdays = useAppSelector(state =>
    getConnectsWorkdaysByUserId(state, { userId, dateRange: LAST_WEEKS_DATE_RANGE }),
  );

  useEffect(() => {
    if (isLoading || isError) return;

    dispatch(
      actions.loadConnectWorkdays({
        userId,
        range: LAST_WEEKS_DATE_RANGE,
      }),
    );
  }, [dispatch, userId, isLoading, isError]);

  const userOfficeWorkdays = useMemo(() => {
    return userWorkdays?.filter(isOfficeWorkday) ?? [];
  }, [userWorkdays]);

  const uniqueOfficeDayDates = useMemo(() => {
    return new Set(userOfficeWorkdays.map(entry => dateKeyForDateTimeString(entry.localStart)));
  }, [userOfficeWorkdays]);

  const totalOfficeDays = uniqueOfficeDayDates.size;
  const weeklyAverage = useMemo(() => {
    return Number((totalOfficeDays / WEEKS_BACK).toFixed(1));
  }, [totalOfficeDays]);

  return (
    <div className="grid gap-5 grid-cols-2">
      <div className="border rounded-lg+ p-5 gap-1 flex flex-col">
        <Icon
          icon="calendarCheck"
          className="size-5"
        />
        <p className="text-mapiq-black-500">{t("groups:BookingHistory.WeeklyAverage", { count: weeklyAverage })}</p>

        <CardDayValue
          isLoading={isLoading}
          value={t("groups:BookingHistory.Day", { count: weeklyAverage })}
        />
      </div>

      <div className="border rounded-lg+ p-5 gap-1 flex flex-col">
        <Icon
          className="size-5"
          icon="office"
        />
        <p className="text-mapiq-black-500">{t("groups:BookingHistory.TotalOfficeDays", { count: totalOfficeDays })}</p>
        <CardDayValue
          isLoading={isLoading}
          value={t("groups:BookingHistory.Day", { count: totalOfficeDays })}
        />
      </div>
    </div>
  );
};

const CardDayValue = ({ isLoading, value }: { isLoading: boolean; value: React.ReactNode }) => {
  const { t } = useTranslation();

  return (
    <h4 className="flex gap-1">
      {isLoading ? (
        <>
          <Loader className="size-5" /> <span className="lowercase">{t("translation:Day_other")}</span>
        </>
      ) : (
        value
      )}
    </h4>
  );
};
